import React from "react"
import { PostResponse } from "@fider/models"
import { Icon, Markdown, UserName, Moment, Avatar } from "@fider/components"
import HeroIconDuplicate from "@fider/assets/images/heroicons-duplicate.svg"
import HeroIconSparkles from "@fider/assets/images/heroicons-sparkles-outline.svg"
import { HStack, VStack } from "./layout"
import { useFider } from "@fider/hooks"

type Size = "micro" | "small" | "xsmall" | "normal"

interface PostResponseProps {
  status: string
  response: PostResponse | null
  size?: Size
}

export const ResponseDetails = (props: PostResponseProps): JSX.Element | null => {
  const fider = useFider()
  const isDuplicate = props.status === "duplicate"

  if (!props.response) {
    return null
  }

  return (
    <HStack spacing={4} align="start" className="c-response-details">
      <Avatar user={props.response.user} size="large" />
      <div className="c-response-details__card">
        <div className="c-response-details__inner">
          <VStack spacing={2}>
            <HStack spacing={2} align="center">
              <UserName user={props.response.user} />
              <span className="text-xs text-gray-600">•</span>
              <Moment className="text-xs text-gray-600" locale={fider.currentLocale} date={props.response.respondedAt} />
              <ResponseLozenge status={props.status} response={props.response} size="xsmall" />
            </HStack>

            {props.response?.text && !isDuplicate && (
              <div className="c-response-details__content">
                <Markdown text={props.response.text} style="full" />
              </div>
            )}

            {isDuplicate && props.response.original && (
              <div className="c-response-details__content">
                <a className="text-link" href={`/posts/${props.response.original.number}/${props.response.original.slug}`}>
                  {props.response.original.title}
                </a>
              </div>
            )}
          </VStack>
        </div>
      </div>
    </HStack>
  )
}

// Custom statuses are admin-colored (arbitrary hex), not drawn from the
// project's curated utility-class palette, so their lozenge colors are
// computed inline from the hex rather than via bg-*/text-*/border-* classes.
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const num = parseInt(hex, 16)
  if (isNaN(num)) {
    return { r: 100, g: 116, b: 139 } // slate-ish fallback
  }
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

const getLozengeStyle = (hex: string): { icon: SpriteSymbol; background: string; border: string; color: string } => {
  const { r, g, b } = hexToRgb(hex)
  return {
    icon: HeroIconSparkles,
    background: `rgba(${r}, ${g}, ${b}, 0.12)`,
    border: `rgba(${r}, ${g}, ${b}, 0.35)`,
    color: `rgb(${r}, ${g}, ${b})`,
  }
}

export const ResponseLozenge = (props: PostResponseProps): JSX.Element | null => {
  const fider = useFider()
  const isDuplicate = props.status === "duplicate"
  const found = fider.session.getPostStatus(props.status)
  const displayName = isDuplicate ? "Duplicate" : props.status === "deleted" ? "Deleted" : found?.name || props.status
  const { icon, background, border, color } = getLozengeStyle(found?.color || "64748B")
  const statusIcon = isDuplicate ? HeroIconDuplicate : icon

  const style = { color }

  if (props.size == "micro") {
    return (
      <span className="text-sm" style={style}>
        {displayName}
      </span>
    )
  }

  if (props.size === "xsmall") {
    return (
      <div>
        <HStack align="center" className="rounded-full p-0 px-3" style={{ ...style, background }}>
          <Icon sprite={statusIcon} className="h-4" style={style} />
          <span className="text-xs uppercase" style={style}>
            {displayName}
          </span>
        </HStack>
      </div>
    )
  }

  return (
    <div>
      <HStack align="center" className="border rounded-full p-1 px-3" style={{ ...style, background, borderColor: border }}>
        {!props.size && <Icon sprite={statusIcon} className="h-5" style={style} />}
        <span className={props.size === "small" ? "text-sm" : "text-semibold"} style={style}>
          {displayName}
        </span>
      </HStack>
    </div>
  )
}
